import { useState } from 'react';
import type { CategoryScoringDto, RssItemSummaryDto } from '../types';
import './StatusPage.css';

interface ScoringBreakdownProps {
  categories: CategoryScoringDto[];
  selectedCategory: string | null;
  selectedSubCategory: string | null;
}

interface GroupedScoring {
  categoryName: string;
  subCategoryName: string | null;
  stats: CategoryScoringDto['stats'];
  topScoredItems: RssItemSummaryDto[];
}

export function ScoringBreakdown({ categories, selectedCategory, selectedSubCategory }: ScoringBreakdownProps) {
  // Filter categories based on selection
  const filteredCategories = categories.filter(c => {
    if (selectedCategory && c.categorySlug !== selectedCategory) return false;
    if (selectedSubCategory && c.subCategorySlug !== selectedSubCategory) return false;
    return true;
  });

  // Group scoring data by category/subcategory
  const groupedScoring: GroupedScoring[] = filteredCategories.map(category => ({
    categoryName: category.categoryName,
    subCategoryName: category.subCategoryName,
    stats: category.stats,
    topScoredItems: category.topScoredItems || [],
  }));

  // Calculate aggregate scoring metrics for overview
  const totalItems = filteredCategories.reduce((sum, c) => sum + c.stats.totalItems, 0);
  const selectedItems = filteredCategories.reduce((sum, c) => sum + c.stats.selectedItems, 0);
  const avgScore = filteredCategories.length > 0
    ? filteredCategories.reduce((sum, c) => sum + c.stats.averageScore, 0) / filteredCategories.length
    : 0;

  // State for collapsible sections
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(groupedScoring.map((_, index) => `group-${index}`))
  );

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Interest score components (from CLAUDE.md)
  const components = [
    {
      name: 'Source Weight',
      weight: 0.30,
      description: 'Pre-configured authority of the news source',
      color: 'accent',
    },
    {
      name: 'Frequency',
      weight: 0.35,
      description: 'Cross-source coverage (cluster size)',
      color: 'primary',
    },
    {
      name: 'Recency',
      weight: 0.20,
      description: 'Exponential decay: e^(-0.08 x hours old)',
      color: 'success',
    },
    {
      name: 'Title Signals',
      weight: 0.15,
      description: 'Keywords (breaking, exclusive, opinion, etc.)',
      color: 'warning',
    },
  ];

  const renderTopItems = (items: RssItemSummaryDto[], categoryName: string, subCategoryName: string | null) => {
    if (items.length === 0) return null;

    const title = subCategoryName
      ? `${categoryName} > ${subCategoryName} - Top Scored Items`
      : `${categoryName} - Top Scored Items`;

    return (
      <div className="scoring-breakdown__examples">
        <h5 className="scoring-breakdown__subsection-title">{title}</h5>
        <div className="scoring-breakdown__items-list">
          {items.slice(0, 5).map((item, index) => (
            <div key={item.id} className="scoring-breakdown__item">
              <div className="scoring-breakdown__item-rank">{index + 1}</div>
              <div className="scoring-breakdown__item-content">
                <h6 className="scoring-breakdown__item-title">{item.title}</h6>
                <div className="scoring-breakdown__item-meta">
                  <span className="scoring-breakdown__item-source">{item.sourceName}</span>
                  <span className="scoring-breakdown__item-separator">•</span>
                  <span className="scoring-breakdown__item-score">
                    Score: {item.interestScore.toFixed(3)}
                  </span>
                  {item.isSelected && (
                    <>
                      <span className="scoring-breakdown__item-separator">•</span>
                      <span className="scoring-breakdown__item-selected">Selected</span>
                    </>
                  )}
                  {item.publishedAt && (
                    <>
                      <span className="scoring-breakdown__item-separator">•</span>
                      <span className="scoring-breakdown__item-date">
                        {new Date(item.publishedAt).toLocaleDateString('en-GB', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="scoring-breakdown">
      <div className="scoring-breakdown__panel">
        <div className="scoring-breakdown__header">
          <h3 className="scoring-breakdown__title">Interest Score Algorithm</h3>
          <p className="scoring-breakdown__subtitle">
            Composite score determining article selection priority
          </p>
        </div>

        {avgScore > 0 && (
          <div className="scoring-breakdown__summary">
            <div className="scoring-breakdown__summary-stat">
              <span className="scoring-breakdown__summary-value">{avgScore.toFixed(3)}</span>
              <span className="scoring-breakdown__summary-label">Average Score</span>
            </div>
            <div className="scoring-breakdown__summary-stat">
              <span className="scoring-breakdown__summary-value">{totalItems}</span>
              <span className="scoring-breakdown__summary-label">Total Items</span>
            </div>
            <div className="scoring-breakdown__summary-stat">
              <span className="scoring-breakdown__summary-value">{selectedItems}</span>
              <span className="scoring-breakdown__summary-label">Items Selected</span>
            </div>
          </div>
        )}

        <div className="scoring-breakdown__components">
          <h4 className="scoring-breakdown__section-title">Score Components</h4>
          {components.map((component) => {
            const percentage = (component.weight * 100).toFixed(0);
            return (
              <div key={component.name} className="scoring-breakdown__component">
                <div className="scoring-breakdown__component-header">
                  <div className="scoring-breakdown__component-info">
                    <span className="scoring-breakdown__component-name">{component.name}</span>
                    <span className="scoring-breakdown__component-description">
                      {component.description}
                    </span>
                  </div>
                  <div className="scoring-breakdown__component-weight">
                    <span className="scoring-breakdown__weight-value">{component.weight.toFixed(2)}</span>
                    <span className="scoring-breakdown__weight-badge">{percentage}%</span>
                  </div>
                </div>
                <div className="scoring-breakdown__component-bar">
                  <div
                    className={`scoring-breakdown__component-fill scoring-breakdown__component-fill--${component.color}`}
                    style={{ width: `${percentage}%` }}
                  >
                    <span className="scoring-breakdown__component-fill-label">{percentage}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="scoring-breakdown__formula">
          <h4 className="scoring-breakdown__section-title">Formula</h4>
          <div className="scoring-breakdown__formula-box">
            <code className="scoring-breakdown__formula-code">
              Score = (SourceWeight x 0.30) + (Frequency x 0.35) + (Recency x 0.20) + (TitleSignals x 0.15)
            </code>
          </div>
        </div>
      </div>

      {groupedScoring.length === 0 && (
        <div className="scoring-breakdown__empty">
          <p>No scoring data available for the selected filters</p>
        </div>
      )}

      {groupedScoring.map((group, index) => {
        const groupId = `group-${index}`;
        const isExpanded = expandedGroups.has(groupId);
        const groupTitle = group.subCategoryName
          ? `${group.categoryName} > ${group.subCategoryName}`
          : group.categoryName;

        return (
          <div key={groupId} className="scoring-breakdown__group">
            <div
              className="scoring-breakdown__group-header"
              onClick={() => toggleGroup(groupId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleGroup(groupId);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <h4 className="scoring-breakdown__group-title">{groupTitle}</h4>
              <div className="scoring-breakdown__group-info">
                <span className="scoring-breakdown__group-score">
                  Avg: {group.stats.averageScore.toFixed(3)}
                </span>
                <span className="scoring-breakdown__group-count">
                  {group.stats.selectedItems}/{group.stats.totalItems} selected
                </span>
                <button
                  className="scoring-breakdown__group-toggle"
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? '−' : '+'}
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="scoring-breakdown__group-content">
                <div className="scoring-breakdown__group-stats">
                  <div className="scoring-breakdown__stat">
                    <span className="scoring-breakdown__stat-label">Min:</span>
                    <span className="scoring-breakdown__stat-value">{group.stats.minScore.toFixed(3)}</span>
                  </div>
                  <div className="scoring-breakdown__stat">
                    <span className="scoring-breakdown__stat-label">Median:</span>
                    <span className="scoring-breakdown__stat-value">{group.stats.medianScore.toFixed(3)}</span>
                  </div>
                  <div className="scoring-breakdown__stat">
                    <span className="scoring-breakdown__stat-label">Max:</span>
                    <span className="scoring-breakdown__stat-value">{group.stats.maxScore.toFixed(3)}</span>
                  </div>
                </div>
                {renderTopItems(group.topScoredItems, group.categoryName, group.subCategoryName)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
